import { z } from 'zod';

import { defineFlowSpec, FlowErrorSchema } from '@elara/core/flows';
import { toDateOnlyString } from '@elara/core/time-utils';
import { zDateString } from '@elara/core/zod-utils';

import { ChatModelSpec } from '#models/ChatModelSpec.js';
import { ItineraryItemModelSpec } from '#models/ItineraryItemModelSpec.js';
import { PlaceModelSpec } from '#models/PlaceModelSpec.js';
import { FileAttachmentSchema } from '#models/schemas/attachment-schemas.js';
import {
  BookingAddressSchema,
  HotelBookingRoomSchema,
  LeadGuestSchema,
  PriceBreakdownSchema,
  PriceCheckResultSchema,
  PriceSnapshotItemSchema,
  RoomEligibleExtrasSchema,
} from '#models/schemas/booking-schemas.js';
import { StateCodeSchema } from '#models/schemas/destination-schemas.js';
import { GoogleFlightsBookingOptionsResultsSchema, SearchGoogleFlightsSchema } from '#models/schemas/flight-schemas.js';
import { ItineraryDaySchema, ItineraryItemEventSchema } from '#models/schemas/itinerary-item-event-schemas.js';
import {
  AddItineraryItemInputItemSchema,
  ItineraryItemUpdateSchema,
  ListItineraryItemOutputSchema,
} from '#models/schemas/itinerary-item-schemas.js';
import { ClientMessagePartSchema, MessagePartSchema } from '#models/schemas/message-schemas.js';
import {
  HotelPaymentSelectionSchema,
  PaymentMethodMetadataShape,
  PciVaultTokenSchema,
} from '#models/schemas/payment-method-schemas.js';
import { PlaceSchema } from '#models/schemas/place-schemas.js';
import { OccupancySchema, RoomOptionSchema } from '#models/schemas/room-schemas.js';
import { CostSchema, PreferenceCategorySchema } from '#models/schemas/shared-schemas.js';
import {
  ShortlistGroupSchema,
  ShortlistItemBaseSchema,
  ShortlistItemTypeSchema,
} from '#models/schemas/shortlist-schemas.js';
import { WebSearchResponseSchema, WebSearchSchema } from '#models/schemas/tools.js';
import { AdditionalTravellerSchema } from '#models/schemas/TravelerProfileSchema.js';
import { GetWeatherResponse } from '#models/schemas/weather-schemas.js';
import { PreferenceProposalChoiceSchema } from '#models/schemas/widget-schemas.js';
import { ShortlistGroupModelSpec } from '#models/ShortlistGroupModelSpec.js';
import { ShortlistItemModelSpec } from '#models/ShortlistItemModelSpec.js';
import { TripModelSpec } from '#models/TripModelSpec.js';
import { UserProfileSpec } from '#models/UserProfileSpec.js';
import { SubscriptionPlanEnum, SubscriptionStatusEnum } from '#subscription.js';

export type * from 'zod';

/**
 * Lightweight warmup flow to pre-heat Cloud Functions on user login.
 * Returns server timestamp to confirm the function is responsive.
 * Called fire-and-forget by the client when auth state changes to logged-in.
 */
export const WarmupFlowSpec = defineFlowSpec({
  name: 'Warmup',
  // Pure keepalive, so the transport may resend it once after a network-level failure — the first
  // request after resuming from the background is the one most likely to hit a dead connection (ELARA-1146).
  idempotent: true,
  // Reachable with a lapsed membership. Boot/keepalive, carries no member data — and it runs before anything knows who is locked.
  allowsLockedAccount: true,
  output: z.object({ timestamp: z.string(), cold: z.boolean() }),
});
export type WarmupFlowSpec = typeof WarmupFlowSpec;

/**
 * The contract for the "CreateTrip" flow.
 * @param input - User data including the user's name.
 * @param output - The full data of the newly created trip document, including its ID and path.
 */
export const CreateTripFlowSpec = defineFlowSpec({
  name: 'CreateTrip',
  // Sensitive action — gated behind legal-policy re-acceptance (defense-in-depth).
  requiresPolicyAcceptance: true,
  input: z.object({ userData: z.object({ name: z.string() }) }),
  output: TripModelSpec.schema
    .extend({ id: z.string(), path: z.string() })
    .describe('The data of the created trip including its path'),
});
export type CreateTripFlowSpec = typeof CreateTripFlowSpec;

/**
 * The contract for the "UpdateTrip" flow.
 * @param input - Trip ID and partial trip data to update
 * @param output - Success boolean
 */
export const UpdateTripFlowSpec = defineFlowSpec({
  name: 'UpdateTrip',
  input: z
    .object({
      tripId: z.string(),
      title: z.string().optional(),
      destinationIds: z.array(z.string()).optional(),
      startDate: zDateString
        .nullable()
        .optional()
        .describe('Valid date string (ISO format or other supported formats)'),
      endDate: zDateString.nullable().optional().describe('Valid date string (ISO format or other supported formats)'),
    })
    // Parse-at-the-boundary: when both dates are supplied in the same request, enforce
    // the startDate <= endDate invariant so a malformed range can never reach the server
    // handler. Partial updates (only one date present) are validated against the persisted
    // value inside UpdateTripFlow, where the other side of the range is known.
    .refine(
      ({ startDate, endDate }) =>
        startDate == null || endDate == null || toDateOnlyString(startDate) <= toDateOnlyString(endDate),
      { message: 'Trip startDate must be on or before endDate', path: ['endDate'] },
    ),
  output: z.object({ success: z.boolean() }),
});
export type UpdateTripFlowSpec = typeof UpdateTripFlowSpec;

/**
 * The contract for the "CreateChat" flow.
 * @param input - Trip ID and user data including the user's name.
 * @param output - The full data of the newly created chat document, including its ID and path.
 */
export const CreateChatFlowSpec = defineFlowSpec({
  name: 'CreateChat',
  input: z.object({
    tripId: z.string().describe('Required reference to parent Trip'),
    userData: z.object({ name: z.string() }),
  }),
  output: ChatModelSpec.schema
    .extend({ id: z.string(), path: z.string() })
    .describe('The data of the created chat including its path'),
});
export type CreateChatFlowSpec = typeof CreateChatFlowSpec;

export const ReasoningEffortSchema = z.enum(['none', 'minimal', 'low', 'medium', 'high', 'xhigh', 'max']);
export type ReasoningEffort = z.infer<typeof ReasoningEffortSchema>;

export const SkillVersionsSchema = z.record(z.string(), z.number().int().positive());
export type SkillVersions = z.infer<typeof SkillVersionsSchema>;

/**
 * The contract for the "SendMessage" flow.
 * @param input - The `chatId` and the `content` of the message as a non-empty array of `UserPartSchema`.
 * @param output - The `messageId` of the user's newly created message.
 * @param chunk - The `MessagePartSchema` for streaming back the AI's response.
 */
export const SendMessageFlowSpec = defineFlowSpec({
  name: 'SendMessage',
  // Sensitive action — gated behind legal-policy re-acceptance (defense-in-depth).
  requiresPolicyAcceptance: true,
  input: z.object({
    id: z.string(),
    chatId: z.string(),
    replyId: z.string(),
    sequence: z.number(),
    agent: z.string().default('concierge'),
    content: z.array(ClientMessagePartSchema),
    /** Optional model override for privileged users (simplified ID, e.g., "opus", "sonnet") */
    modelId: z.string().nullish(),
    promptVersion: z.number().int().positive().nullish(),
    reasoningEffort: ReasoningEffortSchema.nullish(),
    skillVersions: SkillVersionsSchema.nullish(),
  }),
  output: z.object({ messageId: z.string(), replyId: z.string() }),
  chunk: z.union([MessagePartSchema, FlowErrorSchema]),
});
export type SendMessageFlowSpec = typeof SendMessageFlowSpec;

/**
 * The contract for the "StopGeneration" flow.
 *
 * Lets a user interrupt an in-progress AI reply. The client sends the `chatId` and the
 * `replyId` of the streaming reply (the same client-generated id it passed to SendMessage).
 * The server flags the reply doc so the instance generating it (identified across Cloud Run
 * instances via Firestore, not the connection) aborts and persists the partial reply as stopped.
 *
 * @param input - The `chatId` and the `replyId` of the reply to stop.
 * @param output - Whether a stop was requested (`false` when the reply was already finalized).
 */
export const StopGenerationFlowSpec = defineFlowSpec({
  name: 'StopGeneration',
  input: z.object({ chatId: z.string(), replyId: z.string() }),
  output: z.object({ stopped: z.boolean(), preGeneration: z.boolean() }),
});
export type StopGenerationFlowSpec = typeof StopGenerationFlowSpec;

/**
 * The contract for the "SubmitMessageFeedback" flow.
 * Stores per-message user feedback (good/bad rating and optional comment) and triggers server-side telemetry.
 */
export const SubmitMessageFeedbackFlowSpec = defineFlowSpec({
  name: 'SubmitMessageFeedback',
  input: z.object({
    chatId: z.string(),
    messageId: z.string(),
    rating: z.enum(['good', 'bad']),
    comment: z.string().max(4000).optional(),
  }),
  output: z.object({ ok: z.boolean() }),
});
export type SubmitMessageFeedbackFlowSpec = typeof SubmitMessageFeedbackFlowSpec;

/**
 * The contract for the "GenerateChatTitle" flow.
 * @param input - Chat ID to generate title for.
 * @param output - The generated title and whether it was newly generated.
 */
export const GenerateChatTitleFlowSpec = defineFlowSpec({
  name: 'GenerateChatTitle',
  input: z.object({ chatId: z.string() }),
  output: z.object({ title: z.string(), generated: z.boolean() }),
});
export type GenerateChatTitleFlowSpec = typeof GenerateChatTitleFlowSpec;

/**
 * Why profile creation refused to proceed. Each reason maps to a distinct, actionable message on the
 * client; collapsing them into one "invalid invite" message left members retrying a code that had
 * already been redeemed (or, after an admin deleted their first account, one that could never work).
 */
export const CreateUserProfileRejectionReasonSchema = z.enum([
  'invite_missing',
  'invite_not_found',
  'invite_disabled',
  'invite_expired',
  'invite_already_used',
  'policy_version_stale',
]);
export type CreateUserProfileRejectionReason = z.infer<typeof CreateUserProfileRejectionReasonSchema>;

/**
 * The contract for the "CreateUserProfile" flow.
 * @param input - Invite code and the policy versions the user accepted at signup.
 * @param output - `created` when a profile was written, `already_exists` when the caller already had
 *   one (idempotent re-invocation), or `rejected` with the reason the invite/policy check failed.
 */
export const CreateUserProfileFlowSpec = defineFlowSpec({
  name: 'CreateUserProfile',
  // Reachable with a lapsed membership. Signup: the profile (and with it the membership) is being written right here.
  allowsLockedAccount: true,
  input: z.object({ inviteCode: z.string(), acceptedTermsVersion: z.string(), acceptedPrivacyVersion: z.string() }),
  output: z.discriminatedUnion('status', [
    z.object({ status: z.literal('created') }),
    z.object({ status: z.literal('already_exists') }),
    z.object({ status: z.literal('rejected'), reason: CreateUserProfileRejectionReasonSchema }),
  ]),
});
export type CreateUserProfileFlowSpec = typeof CreateUserProfileFlowSpec;

/**
 * The contract for the "AcceptPolicies" flow.
 * Records the user's acceptance of the current Terms of Service and Privacy Policy versions
 * on their existing profile. Used by the in-app re-acceptance dialog when policies are revised.
 */
export const AcceptPoliciesFlowSpec = defineFlowSpec({
  name: 'AcceptPolicies',
  // Reachable with a lapsed membership. An exit. A member must be able to accept revised terms whatever their billing says.
  allowsLockedAccount: true,
  input: z.object({ termsVersion: z.string(), privacyVersion: z.string() }),
  output: z.object({ success: z.literal(true) }),
});
export type AcceptPoliciesFlowSpec = typeof AcceptPoliciesFlowSpec;

/**
 * The contract for the "ValidateInviteCode" flow.
 * @param input - Invite code to validate
 * @param output - Whether the code is valid
 */
export const ValidateInviteCodeFlowSpec = defineFlowSpec({
  name: 'ValidateInviteCode',
  // Reachable with a lapsed membership. Signup, before a membership exists.
  allowsLockedAccount: true,
  input: z.object({ inviteCode: z.string() }),
  // `plan` is advisory UI only — it decides whether signup renders card fields. Present only when
  // the code is valid, so an invalid guess reveals nothing. The authoritative plan is re-read from
  // the code document server-side inside CreateUserProfileFlow's consumption transaction: tampering
  // with this value can only hide the payment form, never obtain a free membership.
  output: z.object({ valid: z.boolean(), plan: SubscriptionPlanEnum.optional() }),
});
export type ValidateInviteCodeFlowSpec = typeof ValidateInviteCodeFlowSpec;

/**
 * The contract for the "UpdateUserProfile" flow.
 * @param input - Partial user profile data to update
 * @param output - Success boolean
 */
export const UpdateUserProfileSpec = defineFlowSpec({
  name: 'UpdateUserProfile',
  // Excludes createdAt/updatedAt: Zod v4 applies schema defaults even to keys absent from the input
  // object, so including them here would re-stamp createdAt on every update (they're system-managed,
  // never caller-set).
  // Excludes deletionRequestedAt: it is server-authoritative — set only by RequestAccountDeletionFlow
  // (and cleared by admin deletion). A client must not be able to set or clear its own pending-deletion
  // state via a generic profile update.
  // Excludes email: changing the profile copy alone leaves Firebase Auth (the sign-in authority),
  // Stripe, Resend, verification state, and sessions out of sync. Email corrections are privileged
  // and go through AdminCorrectUserEmail.
  // Excludes stripeCustomerId + subscription for the same reason: `subscription` is the sole input to
  // the account lock, so a client that could PATCH it would grant itself a free active membership.
  // Excludes smsWelcomeSentAt: it is SendSmsWelcomeFlow's send-once marker, so clearing it would
  // let a client make the Elara number text it again.
  input: UserProfileSpec.schema
    .omit({
      createdAt: true,
      updatedAt: true,
      email: true,
      deletionRequestedAt: true,
      smsWelcomeSentAt: true,
      stripeCustomerId: true,
      subscription: true,
    })
    .partial(),
  output: z.boolean(),
});
export type UpdateUserProfileSpec = typeof UpdateUserProfileSpec;

/** Contract for a client decision that writes or declines a proposed durable preference. */
export const DecidePreferenceProposalFlowSpec = defineFlowSpec({
  name: 'DecidePreferenceProposal',
  input: z.object({
    chatId: z.string(),
    messageId: z.string(),
    proposalId: z.string(),
    decision: PreferenceProposalChoiceSchema,
  }),
  output: z.object({ decision: PreferenceProposalChoiceSchema }),
});
export type DecidePreferenceProposalFlowSpec = typeof DecidePreferenceProposalFlowSpec;

/** Which person on the profile a preference belongs to: the member (omit it) or the companion with this id. */
const PreferenceOwnerSchema = z.object({ travellerId: z.string().min(1).optional() });

/**
 * Adds one categorized preference to the member's or a companion's `preferences`, written in a
 * transaction so it can't overwrite (or be overwritten by) a concurrent profile save or remembered fact.
 */
export const AddProfilePreferenceFlowSpec = defineFlowSpec({
  name: 'AddProfilePreference',
  input: PreferenceOwnerSchema.extend({ value: z.string().trim().min(1), category: PreferenceCategorySchema }),
  output: z.object({ id: z.string() }),
});
export type AddProfilePreferenceFlowSpec = typeof AddProfilePreferenceFlowSpec;

/**
 * Rewords one saved preference, found by id on the member or any companion. "Avoid" entries can't be
 * reworded: their hidden hotel/place reference would no longer match the text, so they're remove-only.
 */
export const UpdateProfilePreferenceFlowSpec = defineFlowSpec({
  name: 'UpdateProfilePreference',
  input: z.object({ id: z.string().min(1), value: z.string().trim().min(1) }),
  output: z.boolean(),
});
export type UpdateProfilePreferenceFlowSpec = typeof UpdateProfilePreferenceFlowSpec;

/** Removes one saved preference, found by id on the member or any companion. */
export const RemoveProfilePreferenceFlowSpec = defineFlowSpec({
  name: 'RemoveProfilePreference',
  input: z.object({ id: z.string().min(1) }),
  output: z.boolean(),
});
export type RemoveProfilePreferenceFlowSpec = typeof RemoveProfilePreferenceFlowSpec;

/**
 * Creates or updates one companion. With an `id`, replaces that companion's details and keeps their
 * stored preferences (those change only through the per-entry preference flows, so any sent here are
 * ignored); without one, adds a new companion — with the preferences typed while creating them — and
 * returns the new id. Written in a transaction so it can't clobber a concurrent edit.
 */
export const SaveTravellerFlowSpec = defineFlowSpec({
  name: 'SaveTraveller',
  input: z.object({ traveller: AdditionalTravellerSchema }),
  output: z.object({ id: z.string() }),
});
export type SaveTravellerFlowSpec = typeof SaveTravellerFlowSpec;

/** Removes one companion, and with them their preferences. */
export const RemoveTravellerFlowSpec = defineFlowSpec({
  name: 'RemoveTraveller',
  input: z.object({ id: z.string().min(1) }),
  output: z.boolean(),
});
export type RemoveTravellerFlowSpec = typeof RemoveTravellerFlowSpec;

/**
 * Begin collecting a card for the membership.
 *
 * Creates a Stripe SetupIntent — NOT a subscription. That ordering is the whole design: a
 * subscription is a billing commitment, and one created before the member has given a card produces
 * states nobody asked for (an entitling `trialing` with no payment method, abandoned attempts that
 * charge later, several subscriptions per customer with no way to say which one counts). A
 * SetupIntent commits to nothing, entitles nobody, and Stripe expires it if it is abandoned.
 *
 * The subscription is created by {@link CompleteMembershipPaymentFlowSpec}, once the card exists.
 *
 * Takes no input, and returns nothing plan-specific, because the card is collected the same way
 * whichever plan is bought: WHICH plan is chosen is a client-side selection until the member
 * commits. Prices for the cards come from {@link GetMembershipPlansFlowSpec}, which creates nothing.
 *
 * The customer always comes from the caller's own profile, never from input, so nothing a client
 * sends can point this at someone else's billing.
 */
export const StartMembershipPaymentFlowSpec = defineFlowSpec({
  name: 'StartMembershipPayment',
  // Reachable with a lapsed membership. A remedy: this is how a locked member stops being locked.
  allowsLockedAccount: true,
  input: z.object({}),
  /**
   * Two outcomes, discriminated so the screen cannot mistake one for the other.
   *
   * `already_active` exists because the profile projection can lag Stripe — a dropped webhook, say —
   * and a member in that state would otherwise be shown a card form for a membership they already
   * have. The flow re-reads Stripe, repairs the projection and reports it.
   */
  output: z.discriminatedUnion('outcome', [
    z.object({
      outcome: z.literal('card_required'),
      /** SetupIntent client secret. The browser confirms it with `stripe.confirmSetup`. */
      clientSecret: z.string().describe('SetupIntent secret for the Payment Element'),
      /**
       * Customer Session secret, which is what lets the Payment Element list the cards this member
       * already has as well as the fields for a new one — the case a returning member restarting
       * their membership is in.
       *
       * Optional because Stripe can refuse to mint one, and a member who cannot pay is a worse
       * outcome than a member who has to retype a card. Absent means the form collects a card the
       * way it did before saved cards existed.
       */
      customerSessionClientSecret: z
        .string()
        .optional()
        .describe('Customer Session secret; enables the saved-card list'),
      /**
       * Whether a plan's trial applies to THIS member. One trial per customer: a returning member is
       * charged from day one. Reported here so the screen never promises a trial the completion step
       * will refuse — both read the same Stripe-backed answer.
       */
      trialEligible: z.boolean().describe('False for a member who has held a membership before'),
    }),
    z.object({
      outcome: z.literal('already_active'),
      planId: SubscriptionPlanEnum.describe('Plan the live subscription is on'),
      status: SubscriptionStatusEnum.describe('Entitling status read from Stripe'),
    }),
  ]),
});
export type StartMembershipPaymentFlowSpec = typeof StartMembershipPaymentFlowSpec;

/**
 * Turn a confirmed card into the membership.
 *
 * Called once `stripe.confirmSetup` has succeeded in the browser. The server re-reads the
 * SetupIntent from Stripe — never trusting the client's word that it succeeded — checks it belongs
 * to the caller's own customer, and only then creates the subscription with that card as its
 * default payment method.
 *
 * Because the subscription is created here, its status is known synchronously and the projection is
 * written from the very object Stripe returned. There is nothing to poll for: no window in which a
 * member has paid but is not yet entitled.
 *
 * `payment_behavior: 'error_if_incomplete'` means a declined card leaves NO subscription behind, so
 * "try another card" is a clean retry rather than a state to clean up.
 */
export const CompleteMembershipPaymentFlowSpec = defineFlowSpec({
  name: 'CompleteMembershipPayment',
  // Reachable with a lapsed membership. A remedy: this is how a locked member stops being locked.
  allowsLockedAccount: true,
  input: z.object({
    /**
     * The SetupIntent the browser just confirmed. Verified against Stripe and against the caller's
     * own customer — a member cannot name someone else's, and cannot claim one that did not succeed.
     */
    setupIntentId: z.string().min(1).describe('SetupIntent confirmed in the browser'),
    /**
     * Which plan to buy. Validated against the catalog rather than trusted: only a plan that is both
     * `selectable` and `requiresPayment` may be named, so this can never self-grant a comped
     * membership. Omitted means the plan already recorded on the profile.
     */
    planId: SubscriptionPlanEnum.optional().describe('Chosen plan; defaults to the profile’s'),
  }),
  /**
   * Three outcomes, all of them real states rather than error codes.
   *
   * `authentication_required` is the narrow SCA case: the first charge runs off-session against the
   * card just authenticated, and an issuer can still demand a challenge. The browser confirms the
   * PaymentIntent named here and the subscription activates.
   */
  output: z.discriminatedUnion('outcome', [
    z.object({
      outcome: z.literal('active'),
      planId: SubscriptionPlanEnum.describe('Plan now subscribed'),
      status: SubscriptionStatusEnum.describe('Status Stripe reported, already projected'),
    }),
    z.object({
      outcome: z.literal('authentication_required'),
      /** PaymentIntent secret for the first invoice, to confirm with `stripe.confirmPayment`. */
      clientSecret: z.string().describe('First invoice’s PaymentIntent secret'),
    }),
    z.object({
      outcome: z.literal('card_declined'),
      /** Stripe’s own message, which is far more actionable than anything generic. */
      message: z.string().describe('Why the card was refused'),
    }),
  ]),
});
export type CompleteMembershipPaymentFlowSpec = typeof CompleteMembershipPaymentFlowSpec;

/**
 * The plans on offer at signup, with what each costs.
 *
 * Read-only and creates nothing, which is the point: rendering a chooser by quoting created
 * subscriptions would mint one incomplete subscription per plan merely to show a price. Takes no
 * input — the catalog decides what is offerable, never the caller.
 *
 * `amount` here is the plan's list price. What a given member is actually charged comes back from
 * `StartMembershipPayment`, which accounts for tax, coupons and a trial's zero first invoice.
 */
export const GetMembershipPlansFlowSpec = defineFlowSpec({
  name: 'GetMembershipPlans',
  // Read-only, so the transport may resend it once after a network-level failure (ELARA-1146).
  idempotent: true,
  // Reachable with a lapsed membership. A remedy: the payment screen cannot price the plans without it.
  allowsLockedAccount: true,
  input: z.object({}),
  output: z.object({
    plans: z.array(
      z.object({
        id: SubscriptionPlanEnum,
        name: z.string(),
        benefits: z.array(
          z.object({
            icon: z.string(),
            text: z.string(),
            firstTimeOnly: z.boolean().nullish().describe('Hide from a member restarting a membership'),
          }),
        ),
        trialDays: z
          .number()
          .int()
          .nullish()
          .describe("Free days before the first charge: the caller's invite trial, on the plan it was granted for"),
        /** Absent when the plan has no price provisioned, or its price carries no single amount. */
        amount: z.number().int().nullish().describe('List price per period, in minor units'),
        currency: z.string().nullish(),
        interval: z.string().nullish(),
        intervalCount: z.number().int().nullish(),
      }),
    ),
  }),
});
export type GetMembershipPlansFlowSpec = typeof GetMembershipPlansFlowSpec;

/**
 * A hosted Stripe billing portal session for the caller's own membership.
 *
 * The exit for a LAPSED member — cancelled, unpaid, or paused. They already have a subscription, so
 * the card form is the wrong remedy: it would mint a second one. The portal is where they update
 * the card, pay the outstanding invoice, or resume, and it is Stripe's own UI, so no card details
 * pass through Elara.
 *
 * Also the route for an entitled member managing their membership — changing card, cancelling,
 * reading invoices — which is why it is not restricted to the lapsed case.
 *
 * Takes no input. The customer comes from the caller's own profile and the return URL from server
 * config, so nothing a client sends can point this at someone else's billing or bounce the member
 * to an arbitrary host afterwards.
 */
export const OpenBillingPortalFlowSpec = defineFlowSpec({
  name: 'OpenBillingPortal',
  // Reachable with a lapsed membership. A remedy: the lapsed member’s only route back.
  allowsLockedAccount: true,
  input: z.object({}),
  output: z.object({
    /** Short-lived Stripe URL. The browser navigates to it; it is not stored anywhere. */
    url: z.string().describe('Hosted billing portal session URL'),
  }),
});
export type OpenBillingPortalFlowSpec = typeof OpenBillingPortalFlowSpec;

/**
 * The contract for the "SyncMembership" flow.
 *
 * Reads the caller's subscription straight from Stripe and writes the profile projection, then
 * reports the status now stored. Takes no input for the same reason `StartMembershipPayment` does:
 * the customer comes from the caller's own profile, so nothing a client sends can point this at
 * someone else's subscription.
 *
 * This is what makes entitlement immediate and recoverable rather than dependent on a webhook
 * delivery. The client calls it as soon as the Payment Element confirms, so a member who has just
 * been charged has access at once instead of waiting on `customer.subscription.updated`; the same
 * call backs the account lock's re-check and a manual ops repair. The webhook keeps handling what
 * happens later with nobody waiting — renewals, dunning, cancellations, Dashboard edits.
 */
export const SyncMembershipFlowSpec = defineFlowSpec({
  name: 'SyncMembership',
  // Reachable with a lapsed membership. A remedy: reads Stripe back, which is how a stale lock repairs itself.
  allowsLockedAccount: true,
  input: z.object({}),
  output: z.object({
    status: SubscriptionStatusEnum.describe('Status now stored on the profile'),
    /** False when Stripe has no subscription for this customer yet, or reported one this app does
     * not model — the caller should keep waiting rather than treat the status as settled. */
    settled: z.boolean().describe('Whether Stripe had a subscription this app could project'),
  }),
});
export type SyncMembershipFlowSpec = typeof SyncMembershipFlowSpec;

/**
 * The contract for the "RequestAccountDeletion" flow.
 *
 * Lets an authenticated member request deletion of their account (App Store / Play Store
 * requirement). Elara is a high-touch, subscription-billed service, so this does NOT perform a
 * self-service hard delete: it marks the profile as deletion-requested, records a durable request
 * for the support team, and emails support (for review) plus the member (confirmation). An admin
 * performs the actual deletion via `AdminDeleteUser` after contacting the member.
 *
 * @param input - Optional free-text reason for the request.
 * @param output - `ok`, plus `alreadyRequested` when a prior request is still pending (idempotent).
 */
export const RequestAccountDeletionFlowSpec = defineFlowSpec({
  name: 'RequestAccountDeletion',
  // Reachable with a lapsed membership. An exit. Locking someone out of leaving would be indefensible.
  allowsLockedAccount: true,
  input: z.object({ reason: z.string().max(2000).optional() }),
  output: z.object({ ok: z.boolean(), alreadyRequested: z.boolean().optional() }),
});
export type RequestAccountDeletionFlowSpec = typeof RequestAccountDeletionFlowSpec;

/**
 * The contract for the "SendSmsWelcome" flow (ELARA-1167).
 *
 * Called by the client right after it links a verified phone to the member's Firebase Auth record.
 * Texts that number from the Elara number with an introduction to the SMS service, at most once per
 * account. Takes no input: the destination is read server-side from the Auth record, so a caller
 * can only ever text its own verified number.
 *
 * @param output - `status`: `sent`; `already_sent` (this account was welcomed before);
 *   `no_verified_phone` (the Auth record has no phone); `unavailable` (outbound messaging is not
 *   configured); `not_delivered` (the provider rejected the send, so a later verification retries).
 */
export const SendSmsWelcomeFlowSpec = defineFlowSpec({
  name: 'SendSmsWelcome',
  input: z.object({}),
  output: z.object({
    status: z.enum(['sent', 'already_sent', 'no_verified_phone', 'unavailable', 'not_delivered']),
  }),
});
export type SendSmsWelcomeFlowSpec = typeof SendSmsWelcomeFlowSpec;

/**
 * The contract for the "GetReferralCode" flow.
 * Returns the user's referral code and pre-filled waitlist URL.
 * Generates a new code on first access (idempotent).
 */
export const GetReferralCodeFlowSpec = defineFlowSpec({
  name: 'GetReferralCode',
  input: z.object({}),
  output: z.object({ referralCode: z.string(), referralUrl: z.string() }),
});
export type GetReferralCodeFlowSpec = typeof GetReferralCodeFlowSpec;

/**
 * The contract for the "CompleteOnboarding" flow.
 * Marks onboarding as complete and creates the user's first trip.
 * @param output - The trip ID and path of the newly created trip.
 */
export const CompleteOnboardingFlowSpec = defineFlowSpec({
  name: 'CompleteOnboarding',
  // Reachable with a lapsed membership. Signup: onboarding runs immediately after the first payment settles.
  allowsLockedAccount: true,
  input: z.object({}),
  output: z.object({ tripId: z.string(), tripPath: z.string() }),
});
export type CompleteOnboardingFlowSpec = typeof CompleteOnboardingFlowSpec;

/**
 * Resets a demo trip back to its original seeded state.
 * Clears all existing data and re-seeds the demo content.
 */
export const ResetDemoTripFlowSpec = defineFlowSpec({
  name: 'ResetDemoTrip',
  input: z.object({ tripId: z.string() }),
  output: z.object({ success: z.boolean() }),
});
export type ResetDemoTripFlowSpec = typeof ResetDemoTripFlowSpec;

/**
 * Creates a new demo trip with seeded data (used when user wants to retake
 * the product tour but no demo trip exists).
 */
export const CreateDemoTripFlowSpec = defineFlowSpec({
  name: 'CreateDemoTrip',
  input: z.object({}),
  output: z.object({ tripId: z.string() }),
});
export type CreateDemoTripFlowSpec = typeof CreateDemoTripFlowSpec;

/** Schema for model options returned to client */
const ModelProviderSchema = z
  .string()
  .min(1)
  .describe('Provider category for UI grouping (e.g., google, anthropic, openai, preset, etc.)');

const ModelOptionSchema = z.object({
  id: z.string().describe('Simplified model ID (e.g., "opus", "sonnet")'),
  label: z.string().describe('Human-readable display label'),
  provider: ModelProviderSchema,
  reasoningEfforts: z.array(ReasoningEffortSchema).optional(),
  defaultReasoningEffort: ReasoningEffortSchema.optional(),
});

/**
 * The contract for the "GetAvailableModels" flow.
 * Returns the list of available models that can be used for model override.
 * This is a privileged-only feature.
 */
export const GetAvailableModelsFlowSpec = defineFlowSpec({
  name: 'GetAvailableModels',
  // Read-only, so the transport may resend it once after a network-level failure (ELARA-1146).
  idempotent: true,
  privileged: true,
  input: z.object({}),
  output: z.object({
    models: z.array(ModelOptionSchema).describe('Flat list of all available models'),
    byProvider: z
      .record(z.string(), z.array(ModelOptionSchema))
      .describe('Models grouped by provider key (google, anthropic, openai, preset, etc.)'),
  }),
});
export type GetAvailableModelsFlowSpec = typeof GetAvailableModelsFlowSpec;

export const PromptVersionOptionSchema = z.object({
  version: z.number().int().positive(),
  labels: z.array(z.string()),
});
export type PromptVersionOption = z.infer<typeof PromptVersionOptionSchema>;

/** The contract for the "GetPromptVersions" flow: an agent's Langfuse prompt versions, newest first. */
export const GetPromptVersionsFlowSpec = defineFlowSpec({
  name: 'GetPromptVersions',
  // Read-only, so the transport may resend it once after a network-level failure (ELARA-1146).
  idempotent: true,
  privileged: true,
  input: z.object({ agent: z.string().default('concierge') }),
  output: z.object({ versions: z.array(PromptVersionOptionSchema) }),
});
export type GetPromptVersionsFlowSpec = typeof GetPromptVersionsFlowSpec;

export const SkillVersionOptionsSchema = z.object({ name: z.string(), versions: z.array(PromptVersionOptionSchema) });
export type SkillVersionOptions = z.infer<typeof SkillVersionOptionsSchema>;

export const GetSkillVersionsFlowSpec = defineFlowSpec({
  name: 'GetSkillVersions',
  // Read-only, so the transport may resend it once after a network-level failure (ELARA-1146).
  idempotent: true,
  privileged: true,
  input: z.object({}),
  output: z.object({ skills: z.array(SkillVersionOptionsSchema) }),
});
export type GetSkillVersionsFlowSpec = typeof GetSkillVersionsFlowSpec;

/**
 * The contract for the "DeleteTrip" flow.
 * @param input - Trip ID to delete (archive).
 * @param output - Success boolean indicating the trip was deleted.
 */
export const DeleteTripFlowSpec = defineFlowSpec({
  name: 'DeleteTrip',
  input: z.object({ tripId: z.string() }),
  output: z.object({ success: z.boolean() }),
});
export type DeleteTripFlowSpec = typeof DeleteTripFlowSpec;

/**
 * The contract for the "RestoreTrip" flow.
 * @param input - Trip ID to restore from archived status.
 * @param output - Success boolean indicating the trip was restored.
 */
export const RestoreTripFlowSpec = defineFlowSpec({
  name: 'RestoreTrip',
  input: z.object({ tripId: z.string() }),
  output: z.object({ success: z.boolean() }),
});
export type RestoreTripFlowSpec = typeof RestoreTripFlowSpec;

/**
 * The contract for the "EnableTripShareLink" flow.
 * Turns on the trip's public read-only itinerary link. Idempotent: returns the existing token while
 * the link is on; a new token is minted only after the link was disabled.
 * @param input - Trip ID (must be owned by the caller).
 * @param output - The share token used to build the public `/share/:token` URL.
 */
export const EnableTripShareLinkFlowSpec = defineFlowSpec({
  name: 'EnableTripShareLink',
  // Publishing trip data to a public URL — gated behind legal-policy re-acceptance (defense-in-depth).
  requiresPolicyAcceptance: true,
  input: z.object({ tripId: z.string() }),
  output: z.object({ token: z.string() }),
});
export type EnableTripShareLinkFlowSpec = typeof EnableTripShareLinkFlowSpec;

/**
 * The contract for the "DisableTripShareLink" flow.
 * Revokes the trip's public itinerary link immediately. Idempotent.
 * @param input - Trip ID (must be owned by the caller).
 * @param output - Success boolean.
 */
export const DisableTripShareLinkFlowSpec = defineFlowSpec({
  name: 'DisableTripShareLink',
  // Reachable with a lapsed membership: a traveler must always be able to take their trip off the public web.
  allowsLockedAccount: true,
  input: z.object({ tripId: z.string() }),
  output: z.object({ success: z.boolean() }),
});
export type DisableTripShareLinkFlowSpec = typeof DisableTripShareLinkFlowSpec;

/**
 * The contract for the "DeleteChat" flow.
 * @param input - Chat ID to delete (archive).
 * @param output - Success boolean indicating the chat was deleted.
 */
export const DeleteChatFlowSpec = defineFlowSpec({
  name: 'DeleteChat',
  input: z.object({ chatId: z.string() }),
  output: z.object({ success: z.boolean() }),
});
export type DeleteChatFlowSpec = typeof DeleteChatFlowSpec;

/**
 * The contract for the "DeleteChatMessage" flow.
 * @param input - Chat ID and message ID to soft-delete.
 * @param output - Success boolean indicating the message was deleted.
 */
export const DeleteChatMessageFlowSpec = defineFlowSpec({
  name: 'DeleteChatMessage',
  privileged: true,
  input: z.object({ chatId: z.string(), messageId: z.string() }),
  output: z.object({ success: z.boolean() }),
});
export type DeleteChatMessageFlowSpec = typeof DeleteChatMessageFlowSpec;

/**
 * The contract for the "RestoreChat" flow.
 * @param input - Chat ID to restore from archived status.
 * @param output - Success boolean indicating the chat was restored.
 */
export const RestoreChatFlowSpec = defineFlowSpec({
  name: 'RestoreChat',
  input: z.object({ chatId: z.string() }),
  output: z.object({ success: z.boolean() }),
});
export type RestoreChatFlowSpec = typeof RestoreChatFlowSpec;

/**
 * The contract for the "WebSearch" flow.
 * @param input - Search query and options for web search
 * @param output - Search results from the web
 */
export const WebSearchFlowSpec = defineFlowSpec({
  name: 'WebSearch',
  input: WebSearchSchema,
  output: WebSearchResponseSchema,
});

export type WebSearchFlowSpec = typeof WebSearchFlowSpec;

/**
 * The contract for the "GetFlightBookingOptions" flow.
 * @param input - Search params including bookingToken (required), origin, destination, dates, currency, locale, etc.
 * @param output - Booking options (providers, prices, booking URLs) for the selected flight.
 */
export const GetFlightBookingOptionsFlowSpec = defineFlowSpec({
  name: 'GetFlightBookingOptions',
  input: SearchGoogleFlightsSchema.and(
    z.object({
      bookingToken: z.string(),
      currency: z.string().optional().describe('Currency code (e.g. USD, EUR). Defaults to USD.'),
      countryCode: z.string().optional().describe('Country/locale code for results (e.g. us, gb). Defaults to us.'),
    }),
  ),
  output: GoogleFlightsBookingOptionsResultsSchema,
});
export type GetFlightBookingOptionsFlowSpec = typeof GetFlightBookingOptionsFlowSpec;

/**
 * The contract for the "GetDestinationWeather" flow.
 * Supports current weather and optional forecast window.
 *
 * Note: `stateCode` accepts both string and number types for flexibility.
 * When reading from destination data, `stateProvinceCode` is normalized to string
 * during schema parsing, but the flow accepts both types to support direct calls.
 */
export const GetDestinationWeatherSpec = defineFlowSpec({
  name: 'GetDestinationWeather',
  // Read-only, so the transport may resend it once after a network-level failure (ELARA-1146).
  idempotent: true,
  input: z.object({
    destinationId: z.string().optional(),
    name: z.string().optional(),
    countryCode: z.string().optional(),
    stateCode: StateCodeSchema.optional(),
    units: z.enum(['metric', 'imperial']).optional(),
    startDate: zDateString.optional(),
    endDate: zDateString.optional().describe('YYYY-MM-DD (inclusive)'),
  }),
  output: GetWeatherResponse,
});
export type GetDestinationWeatherSpec = typeof GetDestinationWeatherSpec;

/**
 * The contract for the "AddItineraryItem" flow.
 *
 * For room items, callers may pass `quantity` (1–10) at the input level to create
 * N independent itinerary items with the same `selectedRateKey` and `occupancy`.
 * Each item is one room. `quantity` is NOT stored on the resulting items.
 */
export const AddItineraryItemFlowSpec = defineFlowSpec({
  name: 'AddItineraryItem',
  input: z.object({
    tripId: z.string(),
    item: AddItineraryItemInputItemSchema,
    quantity: z
      .number()
      .int()
      .min(1)
      .max(10)
      .optional()
      .describe('For room items, number of identical rooms to create. Defaults to 1.'),
  }),
  output: z.object({
    items: z.array(ItineraryItemModelSpec.schema).describe('Created itinerary items (one per requested room).'),
  }),
});
export type AddItineraryItemFlowSpec = typeof AddItineraryItemFlowSpec;

/**
 * The contract for the "UpdateItineraryItem" flow.
 * @param input - Trip ID, item ID, and partial item data to update
 * @param output - Success boolean and the updated item (for badges/linking)
 */
export const UpdateItineraryItemFlowSpec = defineFlowSpec({
  name: 'UpdateItineraryItem',
  input: z.object({
    tripId: z.string(),
    itemId: z.string(),
    updates: ItineraryItemUpdateSchema.describe(
      'Partial updates including optional custom title; when title is omitted the server may generate one',
    ),
  }),
  output: z.object({
    success: z.boolean(),
    item: ItineraryItemModelSpec.schema.describe('The updated itinerary item including its path'),
  }),
});
export type UpdateItineraryItemFlowSpec = typeof UpdateItineraryItemFlowSpec;

/**
 * The contract for the "DeleteItineraryItem" flow.
 * @param input - Trip ID and item ID to delete
 * @param output - Success boolean and the deleted item (for badges/linking)
 */
export const DeleteItineraryItemFlowSpec = defineFlowSpec({
  name: 'DeleteItineraryItem',
  input: z.object({ tripId: z.string(), itemId: z.string() }),
  output: z.object({
    success: z.boolean(),
    item: ItineraryItemModelSpec.schema.describe('The deleted itinerary item including its path'),
  }),
});
export type DeleteItineraryItemFlowSpec = typeof DeleteItineraryItemFlowSpec;

/**
 * The contract for the "RestoreItineraryItem" flow.
 * @param input - Trip ID and item ID to restore
 * @param output - Success boolean
 */
export const RestoreItineraryItemFlowSpec = defineFlowSpec({
  name: 'RestoreItineraryItem',
  input: z.object({ tripId: z.string(), itemId: z.string() }),
  output: z.object({ success: z.boolean() }),
});
export type RestoreItineraryItemFlowSpec = typeof RestoreItineraryItemFlowSpec;

/**
 * The contract for the "ListItineraryItems" flow.
 * @param input - Trip ID
 * @param output - Array of itinerary items (base shape + displayTitle, createdAt, updatedAt from server)
 */
export const ListItineraryItemsFlowSpec = defineFlowSpec({
  name: 'ListItineraryItems',
  // Read-only, so the transport may resend it once after a network-level failure (ELARA-1146).
  idempotent: true,
  input: z.object({ tripId: z.string() }),
  output: z.array(ListItineraryItemOutputSchema),
});
export type ListItineraryItemsFlowSpec = typeof ListItineraryItemsFlowSpec;

/**
 * The contract for the "ListItineraryEvents" flow.
 *
 * Returns the trip's itinerary already expanded into display events
 * (flights fanned out, hotels grouped into check-in/out, places categorized,
 * multi-day items split), pre-sorted, plus the canonical day list
 * (date key + day number + travel-day flag). Computed on read by the shared
 * expansion engine so the day structure matches the AI "Day N" titles and so a
 * native client never re-derives this logic. The client still hydrates each
 * event's source item reactively and owns all date/time formatting.
 *
 * @param input - Trip ID
 * @param output - Expanded events plus the canonical ordered day list
 */
export const ListItineraryEventsFlowSpec = defineFlowSpec({
  name: 'ListItineraryEvents',
  // Read-only, so the transport may resend it once after a network-level failure (ELARA-1146).
  idempotent: true,
  input: z.object({ tripId: z.string() }),
  output: z.object({ events: z.array(ItineraryItemEventSchema), days: z.array(ItineraryDaySchema) }),
});
export type ListItineraryEventsFlowSpec = typeof ListItineraryEventsFlowSpec;

/**
 * The contract for the "BatchUpdateItineraryItems" flow.
 * @param input - Trip ID and array of item updates
 * @param output - Success boolean and count of updated items
 */
export const BatchUpdateItineraryItemsFlowSpec = defineFlowSpec({
  name: 'BatchUpdateItineraryItems',
  input: z.object({
    tripId: z.string(),
    updates: z.array(z.object({ itemId: z.string(), updates: ItineraryItemUpdateSchema })),
  }),
  output: z.object({ success: z.boolean(), updatedCount: z.number() }),
});
export type BatchUpdateItineraryItemsFlowSpec = typeof BatchUpdateItineraryItemsFlowSpec;

/**
 * The contract for the "GenerateItinerarySummaries" flow.
 * Regenerates AI-generated day titles and trip-level summary when itinerary or trip dates change.
 * @param input - Trip ID
 * @param output - Whether summaries were updated and how many days got titles
 */
export const GenerateItinerarySummariesFlowSpec = defineFlowSpec({
  name: 'GenerateItinerarySummaries',
  input: z.object({ tripId: z.string() }),
  output: z.object({
    updated: z.boolean().describe('True if day summaries were regenerated and written to the trip'),
    generatedCount: z.number().describe('Number of days that received a generated title'),
  }),
});
export type GenerateItinerarySummariesFlowSpec = typeof GenerateItinerarySummariesFlowSpec;

/**
 * The contract for the "UpdateBookingAttachments" flow.
 * Updates the attachments array on a booking itinerary item.
 * Available to both regular users (with trip access) and admin users.
 */
export const UpdateBookingAttachmentsFlowSpec = defineFlowSpec({
  name: 'UpdateBookingAttachments',
  input: z.object({
    tripId: z.string(),
    itemId: z.string(),
    attachments: z.array(FileAttachmentSchema).describe('Full list of file attachments'),
  }),
  output: z.object({ success: z.boolean() }),
});
export type UpdateBookingAttachmentsFlowSpec = typeof UpdateBookingAttachmentsFlowSpec;

// =============================================================================
// Room option enrichment
// =============================================================================

/**
 * The contract for the "EnrichRoomOption" flow.
 * Loads a room option, runs Sabre pricecheck + LLM enrichment if not already enriched,
 * persists generated content, and returns the room option (same shape whether already enriched or just enriched).
 */
export const EnrichRoomOptionFlowSpec = defineFlowSpec({
  name: 'EnrichRoomOption',
  input: z.object({ tripId: z.string(), roomOptionId: z.string() }),
  output: RoomOptionSchema.extend({ id: z.string() }).describe(
    'The room option document (enriched or already enriched)',
  ),
});
export type EnrichRoomOptionFlowSpec = typeof EnrichRoomOptionFlowSpec;

/**
 * The contract for the "EnrichHotelPhotos" flow.
 * Client-triggered fallback for hotels rendered without stored photos (e.g. shortlisted before
 * the photo pipeline existed). Runs the same provider fetch as the fetchHotelData background
 * path: writes the head `images` + `imagesMeta` on `hotels/{id}` and the full set to
 * `hotels/{id}/media/gallery`. Idempotent — returns immediately when photos were already fetched.
 */
export const EnrichHotelPhotosFlowSpec = defineFlowSpec({
  name: 'EnrichHotelPhotos',
  input: z.object({ hotelId: z.string() }),
  output: z.object({
    /** Total images now stored for the hotel (0 when no provider had media). */
    imageCount: z.number(),
    /** Provider that supplied the photos; undefined when nothing was fetched. */
    source: z.string().nullish(),
  }),
});
export type EnrichHotelPhotosFlowSpec = typeof EnrichHotelPhotosFlowSpec;

/**
 * The contract for the "RecheckFlightPrice" flow.
 * Re-checks a stored flight option's price and availability via the booking options API.
 * Updates the flight option document with current pricing and availability status.
 */
export const RecheckFlightPriceFlowSpec = defineFlowSpec({
  name: 'RecheckFlightPrice',
  input: z.object({ tripId: z.string(), flightOptionId: z.string() }),
  output: z.object({
    available: z.boolean(),
    priceChanged: z.boolean(),
    previousCost: CostSchema.nullish(),
    currentCost: CostSchema.nullish(),
    fetchedAt: z.string(),
  }),
});
export type RecheckFlightPriceFlowSpec = typeof RecheckFlightPriceFlowSpec;

/**
 * The contract for the "RecheckRoomPrice" flow.
 * Re-checks a stored room option's rates via Sabre pricecheck.
 * Updates per-rate pricing and availability, and room-level availability flags.
 */
export const RecheckRoomPriceFlowSpec = defineFlowSpec({
  name: 'RecheckRoomPrice',
  input: z.object({ tripId: z.string(), roomOptionId: z.string() }),
  output: z.object({
    available: z.boolean(),
    partiallyAvailable: z.boolean(),
    unavailableRateKeys: z.array(z.string()),
    priceChanged: z.boolean(),
    fetchedAt: z.string(),
  }),
});
export type RecheckRoomPriceFlowSpec = typeof RecheckRoomPriceFlowSpec;

// =============================================================================
// Hotel guide room search (ELARA-1092)
// =============================================================================

/** The stages a room search reports while it runs, in order. */
export const RoomSearchStageSchema = z.enum(['searching', 'naming', 'curating', 'saving']);
export type RoomSearchStage = z.infer<typeof RoomSearchStageSchema>;

/** A stage starting, with what the guide shows while it runs. */
export const RoomSearchProgressSchema = z.object({ stage: RoomSearchStageSchema, message: z.string() });
export type RoomSearchProgress = z.infer<typeof RoomSearchProgressSchema>;

/** A new search: the stay and party the traveller chose in the hotel guide. */
const NewHotelRoomSearchSchema = z.object({
  kind: z.literal('search'),
  tripId: z.string(),
  hotelId: z.string().describe('Hotel document id'),
  startDate: z.iso.date().describe('Check-in, YYYY-MM-DD'),
  endDate: z.iso.date().describe('Check-out, YYYY-MM-DD, after check-in'),
  occupancy: OccupancySchema.describe('The party; the search offers rooms that take everyone, or two of one room'),
  chatId: z.string().nullish().describe("The trip's open conversation, whose working memory supplies the client brief"),
});

/**
 * A refresh of a saved search: the server reads the stay and party from the search's own rooms,
 * so a refresh can never write rooms under a stay the search was not for.
 */
const RefreshHotelRoomSearchSchema = z.object({
  kind: z.literal('refresh'),
  tripId: z.string(),
  hotelId: z.string(),
  searchId: z.string().describe('The search to re-price'),
});

/**
 * The contract for the "SearchHotelRooms" flow: a traveller's own room search from the hotel
 * guide. Runs the concierge's Travelport room search (curated picks included), saves every room
 * under the trip with one `searchId`, and streams the stage it is in. The client renders the rooms
 * from Firestore by `searchId`; the output carries the ids and what the search said about them.
 *
 * `status: 'empty'` is an expected outcome (no live pricing for the hotel, nothing bookable for the
 * stay, the supplier unavailable) with `message` written for the traveller.
 */
export const SearchHotelRoomsFlowSpec = defineFlowSpec({
  name: 'SearchHotelRooms',
  input: z.discriminatedUnion('kind', [NewHotelRoomSearchSchema, RefreshHotelRoomSearchSchema]),
  chunk: z.union([RoomSearchProgressSchema, FlowErrorSchema]),
  output: z.object({
    status: z.enum(['completed', 'empty']),
    searchId: z.string().nullish(),
    roomOptionIds: z.array(z.string()).describe('Every room this search saved or re-priced'),
    presentedRoomOptionIds: z.array(z.string()).describe("Elara's picks, in the search's order (empty on a refresh)"),
    summary: z.string().nullish().describe('One line framing the picks'),
    message: z.string().nullish().describe("Why there are no rooms, for the traveller (status 'empty')"),
  }),
});
export type SearchHotelRoomsFlowSpec = typeof SearchHotelRoomsFlowSpec;

/** Where the guide's room search pre-fill came from, most specific first. */
export const RoomSearchDefaultsSourceSchema = z.enum([
  'search',
  'last_search_hotel',
  'last_search_trip',
  'working_memory',
  'trip',
  'none',
]);
export type RoomSearchDefaultsSource = z.infer<typeof RoomSearchDefaultsSourceSchema>;

/**
 * The contract for the "GetRoomSearchDefaults" flow: what to pre-fill the hotel guide's room
 * search with. A `searchId` the guide was opened on wins (its exact stay and party, so the form
 * matches the rooms listed); then the last search of this hotel in the trip (exact stay and party,
 * and its `searchId` so the guide can show those rooms at once), then the trip's last search for the
 * party, then the concierge's working memory, then the trip's own dates.
 */
export const GetRoomSearchDefaultsFlowSpec = defineFlowSpec({
  name: 'GetRoomSearchDefaults',
  input: z.object({
    tripId: z.string(),
    hotelId: z.string(),
    chatId: z.string().nullish(),
    searchId: z.string().nullish().describe('The saved search the guide was opened on ("View all results")'),
  }),
  output: z.object({
    startDate: z.string().nullish(),
    endDate: z.string().nullish(),
    occupancy: OccupancySchema,
    source: RoomSearchDefaultsSourceSchema,
    lastSearch: z
      .object({
        searchId: z.string(),
        fetchedAt: z.string(),
        startDate: z.string(),
        endDate: z.string(),
        occupancy: OccupancySchema.nullish(),
      })
      .nullish()
      .describe('The last search of this hotel in this trip, when there is one'),
  }),
});
export type GetRoomSearchDefaultsFlowSpec = typeof GetRoomSearchDefaultsFlowSpec;

// =============================================================================
// Place enrichment
// =============================================================================

/**
 * The contract for the "EnrichPlace" flow.
 * Generates an AI description and optional advisory for a place if not already enriched
 * (or if the place data has been refreshed since last generation).
 */
export const EnrichPlaceFlowSpec = defineFlowSpec({
  name: 'EnrichPlace',
  input: z.object({ placeId: z.string().describe('Firestore document id under places/') }),
  output: PlaceSchema.extend({ id: z.string() }).describe('The place document (enriched or already enriched)'),
});
export type EnrichPlaceFlowSpec = typeof EnrichPlaceFlowSpec;

/**
 * The contract for the "GeocodeAddress" flow.
 *
 * Resolves a free-form address (or place name) to coordinates via the server-side Google Places
 * integration. The native map widget (ELARA-710) uses this for address-only map points: the
 * `react-native-maps` library ships no geocoder (unlike the web `@googlemaps/js-api-loader`, which
 * bundled Places/geocoding), so the geocoding gap is routed through the server rather than a client
 * SDK. `result` is absent when the address could not be resolved.
 */
export const GeocodeAddressFlowSpec = defineFlowSpec({
  name: 'GeocodeAddress',
  // Read-only, so the transport may resend it once after a network-level failure (ELARA-1146).
  idempotent: true,
  input: z.object({ address: z.string().min(1).describe('Free-form address or place name to geocode') }),
  output: z.object({
    result: z
      .object({ latitude: z.number(), longitude: z.number(), formattedAddress: z.string().optional() })
      .nullish()
      .describe('Resolved coordinates, or absent when the address could not be geocoded'),
  }),
});
export type GeocodeAddressFlowSpec = typeof GeocodeAddressFlowSpec;

// =============================================================================
// Shortlist Flows
// =============================================================================

/**
 * The contract for the "AddShortlistItem" flow.
 * Single input shape: tripId, referenceId, type, optional groupId and metadata (for rooms).
 * Flow builds the stored item (position computed, title optional) server-side. A group holds each
 * entry once: adding one it already holds writes nothing and returns that entry.
 * @param input - Trip ID, referenceId, type, optional groupId and metadata
 * @param output - The created or already-held shortlist item with ID and path, and which of the two it is
 */
export const AddShortlistItemFlowSpec = defineFlowSpec({
  name: 'AddShortlistItem',
  input: z.object({
    tripId: z.string(),
    referenceId: z.string().describe('ID of the referenced entity (hotelId, flightOptionId, etc.)'),
    type: ShortlistItemTypeSchema,
    groupId: z.string().optional().describe('ID of the group to add the item to'),
    metadata: z
      .record(z.string(), z.unknown())
      .optional()
      .describe(
        'For room type: optional occupancy, quantity, and an optional pinned rate (productCode with its occupancy) — RoomShortlistMetadataSchema',
      ),
  }),
  output: z.object({
    success: z.boolean(),
    item: ShortlistItemModelSpec.schema
      .extend({ id: z.string(), path: z.string() })
      .describe('The created shortlist item including its path'),
    alreadyInGroup: z
      .boolean()
      .describe('True when the group already held this entry and `item` is that entry; false when this call created it'),
  }),
});
export type AddShortlistItemFlowSpec = typeof AddShortlistItemFlowSpec;

/**
 * The contract for the "UpdateShortlistItem" flow.
 * @param input - Trip ID, item ID, and partial item data to update
 * @param output - The updated shortlist item (same shape as AddShortlistItem)
 */
export const UpdateShortlistItemFlowSpec = defineFlowSpec({
  name: 'UpdateShortlistItem',
  input: z.object({
    tripId: z.string(),
    itemId: z.string(),
    updates: ShortlistItemBaseSchema.omit({ addedBy: true, updatedBy: true })
      .partial()
      .describe('Partial updates to the shortlist item; who added or edited it is the server\'s to record'),
  }),
  output: z.object({
    success: z.boolean(),
    item: ShortlistItemModelSpec.schema
      .extend({ id: z.string(), path: z.string() })
      .describe('The updated shortlist item including its path'),
  }),
});
export type UpdateShortlistItemFlowSpec = typeof UpdateShortlistItemFlowSpec;

/**
 * The contract for the "RemoveShortlistItem" flow.
 * @param input - Trip ID and item ID to remove
 * @param output - The removed shortlist item (same shape as AddShortlistItem, for linking in UI)
 */
export const RemoveShortlistItemFlowSpec = defineFlowSpec({
  name: 'RemoveShortlistItem',
  input: z.object({ tripId: z.string(), itemId: z.string() }),
  output: z.object({
    success: z.boolean(),
    item: ShortlistItemModelSpec.schema
      .extend({ id: z.string(), path: z.string() })
      .describe('The removed shortlist item including its path'),
  }),
});
export type RemoveShortlistItemFlowSpec = typeof RemoveShortlistItemFlowSpec;

/**
 * The contract for the "ListShortlistItems" flow.
 * @param input - Trip ID
 * @param output - Array of shortlist item summaries (id, type, groupId, position, referenceId, metadata)
 */
export const ListShortlistItemsFlowSpec = defineFlowSpec({
  name: 'ListShortlistItems',
  // Read-only, so the transport may resend it once after a network-level failure (ELARA-1146).
  idempotent: true,
  input: z.object({ tripId: z.string() }),
  output: z.array(
    z.object({
      id: z.string(),
      type: ShortlistItemTypeSchema,
      groupId: z.string().optional(),
      position: z.number(),
      referenceId: z.string().optional(),
      metadata: z.record(z.string(), z.unknown()).optional(),
    }),
  ),
});
export type ListShortlistItemsFlowSpec = typeof ListShortlistItemsFlowSpec;

/**
 * The contract for the "ListShortlistGroups" flow.
 * @param input - Trip ID
 * @param output - Array of shortlist group summaries (id, name, position, path)
 */
export const ListShortlistGroupsFlowSpec = defineFlowSpec({
  name: 'ListShortlistGroups',
  // Read-only, so the transport may resend it once after a network-level failure (ELARA-1146).
  idempotent: true,
  input: z.object({ tripId: z.string() }),
  output: z.array(z.object({ id: z.string(), name: z.string(), position: z.number(), path: z.string().optional() })),
});
export type ListShortlistGroupsFlowSpec = typeof ListShortlistGroupsFlowSpec;

/**
 * The contract for the "CreateShortlistGroup" flow.
 * @param input - Trip ID and group data
 * @param output - The created group with ID and path
 */
export const CreateShortlistGroupFlowSpec = defineFlowSpec({
  name: 'CreateShortlistGroup',
  input: z.object({ tripId: z.string(), group: ShortlistGroupSchema.partial().required({ name: true }) }),
  output: ShortlistGroupModelSpec.schema
    .extend({ id: z.string(), path: z.string() })
    .describe('The created shortlist group including its path'),
});
export type CreateShortlistGroupFlowSpec = typeof CreateShortlistGroupFlowSpec;

/**
 * The contract for the "UpdateShortlistGroup" flow.
 * @param input - Trip ID, group ID, and partial group data to update
 * @param output - Success boolean
 */
export const UpdateShortlistGroupFlowSpec = defineFlowSpec({
  name: 'UpdateShortlistGroup',
  input: z.object({ tripId: z.string(), groupId: z.string(), updates: ShortlistGroupSchema.partial() }),
  output: z.object({ success: z.boolean() }),
});
export type UpdateShortlistGroupFlowSpec = typeof UpdateShortlistGroupFlowSpec;

/**
 * The contract for the "BatchReorderShortlistGroups" flow.
 * @param input - Trip ID and array of group position updates
 * @param output - Success boolean and count of updated groups
 */
export const BatchReorderShortlistGroupsFlowSpec = defineFlowSpec({
  name: 'BatchReorderShortlistGroups',
  input: z.object({ tripId: z.string(), updates: z.array(z.object({ groupId: z.string(), position: z.number() })) }),
  output: z.object({ success: z.boolean(), updatedCount: z.number() }),
});
export type BatchReorderShortlistGroupsFlowSpec = typeof BatchReorderShortlistGroupsFlowSpec;

/**
 * The contract for the "DeleteShortlistGroup" flow.
 * @param input - Trip ID and group ID to delete
 * @param output - Success boolean
 */
export const DeleteShortlistGroupFlowSpec = defineFlowSpec({
  name: 'DeleteShortlistGroup',
  input: z.object({ tripId: z.string(), groupId: z.string() }),
  output: z.object({ success: z.boolean() }),
});
export type DeleteShortlistGroupFlowSpec = typeof DeleteShortlistGroupFlowSpec;

/**
 * The contract for the "BatchReorderShortlistItems" flow.
 * @param input - Trip ID and array of item updates with groupId and position
 * @param output - Success boolean and count of updated items
 */
export const BatchReorderShortlistItemsFlowSpec = defineFlowSpec({
  name: 'BatchReorderShortlistItems',
  input: z.object({
    tripId: z.string(),
    updates: z.array(z.object({ itemId: z.string(), groupId: z.string(), position: z.number() })),
  }),
  output: z.object({ success: z.boolean(), updatedCount: z.number() }),
});
export type BatchReorderShortlistItemsFlowSpec = typeof BatchReorderShortlistItemsFlowSpec;

/**
 * The contract for the "RefreshPlace" flow.
 * Loads a place by Firestore document id, refreshes from provider if stale (>24h or forceRefresh), returns place id.
 * @param input - placeId (Firestore document id)
 * @param output - placeId and optionally the refreshed place data
 */
export const RefreshPlaceSpec = defineFlowSpec({
  name: 'RefreshPlace',
  input: z.object({ placeId: z.string().describe('Firestore document id under places/') }),
  output: z.object({
    placeId: z.string(),
    place: PlaceModelSpec.schema.optional().describe('Refreshed place data when available'),
  }),
});
export type RefreshPlaceSpec = typeof RefreshPlaceSpec;

// =============================================================================
// Notification Flows
// =============================================================================

/**
 * The contract for the "MarkNotificationRead" flow.
 * Marks a single notification as read for the authenticated user.
 * @param input - Notification ID to mark as read.
 * @param output - Success boolean.
 */
export const MarkNotificationReadFlowSpec = defineFlowSpec({
  name: 'MarkNotificationRead',
  input: z.object({ notificationId: z.string() }),
  output: z.object({ success: z.boolean() }),
});
export type MarkNotificationReadFlowSpec = typeof MarkNotificationReadFlowSpec;

/**
 * The contract for the "MarkAllNotificationsRead" flow.
 * Marks all unread notifications as read for the authenticated user.
 * @param input - Empty object.
 * @param output - Success boolean and count of notifications marked as read.
 */
export const MarkAllNotificationsReadFlowSpec = defineFlowSpec({
  name: 'MarkAllNotificationsRead',
  input: z.object({}),
  output: z.object({ success: z.boolean(), count: z.number() }),
});
export type MarkAllNotificationsReadFlowSpec = typeof MarkAllNotificationsReadFlowSpec;

/**
 * The contract for the "RegisterFCMToken" flow.
 * Registers a Firebase Cloud Messaging token for push notifications.
 * @param input - The FCM token to register.
 * @param output - Success boolean.
 */
export const RegisterFCMTokenFlowSpec = defineFlowSpec({
  name: 'RegisterFCMToken',
  // Reachable with a lapsed membership. Runs at app boot behind the native lock screen; writes only a device token.
  allowsLockedAccount: true,
  input: z.object({ token: z.string() }),
  output: z.object({ success: z.boolean() }),
});
export type RegisterFCMTokenFlowSpec = typeof RegisterFCMTokenFlowSpec;

/**
 * The contract for the "ReportChatPresence" flow.
 *
 * Each signed-in device reports whether it is looking at a chat, so the server can skip a
 * new-chat-message push while any of the user's devices is already showing that chat (no phone buzz
 * while the user chats on web). Clients report on every viewing change and heartbeat while a reply is
 * generating; the server treats a report older than {@link CHAT_PRESENCE_TTL_MS} as gone.
 *
 * @param input - The chat, whether this device is viewing it, and this device's install id.
 * @param output - Success boolean.
 */
export const ReportChatPresenceFlowSpec = defineFlowSpec({
  name: 'ReportChatPresence',
  input: z.object({
    chatId: z.string().min(1),
    viewing: z.boolean(),
    /** The device's Firebase Installation ID (or a persisted random fallback) — one presence doc per device. */
    installId: z.string().regex(/^[\w-]{1,64}$/, 'installId must be 1-64 URL-safe characters'),
  }),
  output: z.object({ success: z.boolean() }),
});
export type ReportChatPresenceFlowSpec = typeof ReportChatPresenceFlowSpec;

/** How often a device re-reports presence while it views a chat with a reply generating. */
export const CHAT_PRESENCE_HEARTBEAT_MS = 15_000;

/** A presence report older than this is ignored, so a device that vanished without saying so stops suppressing pushes. */
export const CHAT_PRESENCE_TTL_MS = 35_000;

// =============================================================================
// Wallet / saved payment methods (M4)
// =============================================================================

/**
 * Mint a PCI Booking hosted card-capture session for the client to embed as an iframe.
 * `clientOrigin` MUST be the embedding page's real origin (`window.location.origin`) — PCI Booking
 * targets its `CardSubmitSuccess` postMessage at exactly that origin and the browser drops it on any
 * mismatch. The client origin differs from the server origin, so it can only come from the client.
 */
export const CreateCardCaptureSessionFlowSpec = defineFlowSpec({
  name: 'CreateCardCaptureSession',
  input: z.object({ clientOrigin: z.string().url() }),
  output: z.object({ iframeUrl: z.string() }),
});
export type CreateCardCaptureSessionFlowSpec = typeof CreateCardCaptureSessionFlowSpec;

/**
 * Save a captured card to the user's wallet. The PAN never touches Elara — only the opaque PCI Booking
 * vault token (persisted server-side only) plus safe display metadata. The token is boundary-checked to
 * reject any PAN/CVV-shaped value.
 */
export const AddPaymentMethodFlowSpec = defineFlowSpec({
  name: 'AddPaymentMethod',
  input: z.object({
    ...PaymentMethodMetadataShape,
    pciBookingToken: PciVaultTokenSchema,
    makeDefault: z.boolean().optional().describe('Set this card as the default on save'),
  }),
  output: z.object({ id: z.string() }),
});
export type AddPaymentMethodFlowSpec = typeof AddPaymentMethodFlowSpec;

/** Rename a saved card and/or set it as the default (setting default unsets any prior default). */
export const UpdatePaymentMethodFlowSpec = defineFlowSpec({
  name: 'UpdatePaymentMethod',
  input: z.object({ id: z.string(), nickname: z.string().nullish(), isDefault: z.boolean().optional() }),
  output: z.object({ success: z.boolean() }),
});
export type UpdatePaymentMethodFlowSpec = typeof UpdatePaymentMethodFlowSpec;

/** Remove a saved card — deletes both the metadata doc and the server-only token sibling. */
export const DeletePaymentMethodFlowSpec = defineFlowSpec({
  name: 'DeletePaymentMethod',
  input: z.object({ id: z.string() }),
  output: z.object({ success: z.boolean() }),
});
export type DeletePaymentMethodFlowSpec = typeof DeletePaymentMethodFlowSpec;

// =============================================================================
// Hotel checkout flows
// =============================================================================

/**
 * Streaming progress chunk emitted during hotel-checkout flows. The client
 * uses these to show loading copy ("Checking room availability…").
 */
export const HotelCheckoutChunkSchema = z.object({
  phase: z.enum(['pricechecking', 'finalizing', 'submitting', 'sending_email']),
  message: z.string().optional(),
});
export type HotelCheckoutChunk = z.infer<typeof HotelCheckoutChunkSchema>;

/**
 * The contract for the "PrepareHotelCheckout" flow.
 *
 * Read-only preparation step for the hotel checkout page: resolves each
 * itinerary item's rate, runs Sabre PriceCheck per room, and returns
 * everything the client needs to render the form — without persisting
 * anything. The actual `HotelBooking` doc is only created at submit time.
 *
 * Returns an `idempotencyKey` the client must echo back on
 * `SubmitHotelBooking` so rapid double-clicks don't create duplicate
 * bookings.
 */
export const PrepareHotelCheckoutFlowSpec = defineFlowSpec({
  name: 'PrepareHotelCheckout',
  input: z.object({ tripId: z.string(), itineraryItemIds: z.array(z.string()).min(1) }),
  output: z.object({
    idempotencyKey: z.string(),
    hotelId: z.string(),
    checkInDate: z.string(),
    checkOutDate: z.string(),
    rooms: z.array(HotelBookingRoomSchema),
    priceSnapshot: z.array(PriceSnapshotItemSchema),
    /**
     * Server-computed tax/fee/subtotal split for the whole prepared set. Sum of
     * the per-room `priceSnapshot[].breakdown`. Omitted when rates span multiple
     * currencies or any room lacks a breakdown — the client falls back to the
     * bottom-line total only.
     */
    totalBreakdown: PriceBreakdownSchema.optional(),
    /**
     * Per-room eligible extras, pre-filtered and capped server-side from the
     * room's occupancy + the priced rate's catalog. The client renders these
     * directly instead of re-deriving crib/rollaway eligibility — collapsing the
     * old client-vs-server cap disagreement into a single rule.
     */
    eligibleExtras: z.array(RoomEligibleExtrasSchema).optional(),
    priceCheckResult: z.array(PriceCheckResultSchema).optional(),
  }),
  chunk: z.union([HotelCheckoutChunkSchema, FlowErrorSchema]),
});
export type PrepareHotelCheckoutFlowSpec = typeof PrepareHotelCheckoutFlowSpec;

/**
 * The contract for the "SubmitHotelBooking" flow.
 *
 * Carries the full prepared checkout state (rooms, snapshot, lead guest,
 * address, etc.) plus the prep-time `idempotencyKey`. The server:
 *   1. De-dupes on `(tripId, idempotencyKey)` — replay returns the existing id.
 *   2. Re-runs PriceCheck.
 *   3. On any room unavailable / out-of-inventory → returns
 *      `{ status: 'price_changed', priceCheckResult, rooms }` and creates
 *      **no doc**. The client re-renders against the refreshed state.
 *   4. On clean PriceCheck → emails the fulfillment team, creates the
 *      `HotelBooking` directly in `pending`, back-links itinerary items.
 */
// `HotelPaymentSelectionSchema` / `HotelPaymentSelection` moved to
// `#models/schemas/payment-method-schemas.js` (ELARA-717) — a module that doesn't collide in the
// shared declaration output, so the React Native app can import the type via a referenced project.
// Re-exported here so existing web import sites (`@elara/domain/flows`) keep resolving.
export type { HotelPaymentSelection } from '#models/schemas/payment-method-schemas.js';
export { HotelPaymentSelectionSchema } from '#models/schemas/payment-method-schemas.js';

/**
 * Optional GA4 client identity forwarded by the app so the authoritative server-side `purchase`
 * event (emitted via the Measurement Protocol) joins the same GA4 user/session as the client's
 * behavioural events. All fields are optional — the server falls back to a UID-derived client id.
 */
export const AnalyticsContextSchema = z
  .object({
    clientId: z.string().optional().describe('GA4 web client_id from the browser SDK'),
    appInstanceId: z.string().optional().describe('Firebase app_instance_id from a native app'),
    sessionId: z.string().optional().describe('GA4 session_id of the originating client session'),
  })
  .optional();
export type AnalyticsContext = z.infer<typeof AnalyticsContextSchema>;

export const SubmitHotelBookingFlowSpec = defineFlowSpec({
  name: 'SubmitHotelBooking',
  // Sensitive action — gated behind legal-policy re-acceptance (defense-in-depth).
  requiresPolicyAcceptance: true,
  input: z.object({
    tripId: z.string(),
    idempotencyKey: z.string(),
    itineraryItemIds: z.array(z.string()).min(1),
    hotelId: z.string(),
    checkInDate: z.string(),
    checkOutDate: z.string(),
    leadGuest: LeadGuestSchema,
    address: BookingAddressSchema,
    customerRequest: z.string().optional(),
    attachments: z.array(FileAttachmentSchema).optional(),
    rooms: z.array(HotelBookingRoomSchema),
    priceSnapshot: z.array(PriceSnapshotItemSchema),
    payment: HotelPaymentSelectionSchema,
    analyticsContext: AnalyticsContextSchema,
  }),
  output: z.discriminatedUnion('status', [
    z.object({ status: z.literal('submitted'), bookingGroupId: z.string() }),
    z.object({
      status: z.literal('price_changed'),
      priceCheckResult: z.array(PriceCheckResultSchema),
      rooms: z.array(HotelBookingRoomSchema),
    }),
  ]),
  chunk: z.union([HotelCheckoutChunkSchema, FlowErrorSchema]),
});
export type SubmitHotelBookingFlowSpec = typeof SubmitHotelBookingFlowSpec;

/**
 * The contract for the "CancelHotelBooking" flow.
 *
 * Cancels a live Sabre hotel booking on a single itinerary item (one PNR per
 * room, mirroring the per-item booking model). The server:
 *   1. Loads the item and authorizes trip access.
 *   2. Idempotent: an `already_cancelled` item returns without touching Sabre.
 *   3. Only a `booked` item (a real PNR locator in `booking.reference`) is
 *      cancellable; `approved` (dry-run) / `planned` return `not_cancellable`.
 *   4. Cancels the PNR via Sabre Orders, read-back-verifies the hotel segment
 *      is gone, and writes `status: 'cancelled'` + `cancelledAt`/`cancelledReason`.
 *
 * Cancel is a server-initiated Orders call — NOT card-relayed (no PAN / PCI
 * Booking). Gated by the same `LIVE_BOOKING_ENABLED` kill-switch as booking.
 */
export const CancelHotelBookingFlowSpec = defineFlowSpec({
  name: 'CancelHotelBooking',
  input: z.object({
    tripId: z.string(),
    itineraryItemId: z.string(),
    /** Optional user-supplied cancellation reason, persisted onto the booking. */
    reason: z.string().optional(),
  }),
  output: z.discriminatedUnion('status', [
    z.object({ status: z.literal('cancelled'), itineraryItemId: z.string(), verified: z.boolean() }),
    // A live cancel whose read-back did not verify the segment is gone — non-terminal; the reservation may
    // still be live and an operator/reconciler resolves it (H2).
    z.object({ status: z.literal('cancellation_pending'), itineraryItemId: z.string(), verified: z.boolean() }),
    z.object({ status: z.literal('already_cancelled'), itineraryItemId: z.string() }),
    z.object({ status: z.literal('not_cancellable'), itineraryItemId: z.string(), reason: z.string() }),
  ]),
  chunk: z.union([HotelCheckoutChunkSchema, FlowErrorSchema]),
});
export type CancelHotelBookingFlowSpec = typeof CancelHotelBookingFlowSpec;

// =============================================================================
// Flight checkout flows
// =============================================================================

/**
 * The contract for the "SubmitFlightBooking" flow.
 *
 * Records the user's self-reported external booking. Upserts a FlightBooking
 * keyed by `(userId, flightOptionId)` in `status: 'booked'`, back-links the
 * matching itinerary item, and (if no item exists yet) auto-creates one.
 * The flight checkout page itself no longer creates a doc on entry — the
 * doc is only ever persisted once the user submits.
 */
export const SubmitFlightBookingFlowSpec = defineFlowSpec({
  name: 'SubmitFlightBooking',
  // Sensitive action — gated behind legal-policy re-acceptance (defense-in-depth).
  requiresPolicyAcceptance: true,
  input: z.object({
    tripId: z.string(),
    flightOptionId: z.string(),
    confirmationNumber: z.string().min(1),
    notes: z.string().optional(),
    bookedProvider: z.object({ name: z.string(), logo: z.string().optional() }).optional(),
    /** What the user actually paid; defaults from the FlightOption if absent. */
    actualCost: CostSchema.optional(),
    analyticsContext: AnalyticsContextSchema,
  }),
  output: z.object({ itineraryItemId: z.string() }),
});
export type SubmitFlightBookingFlowSpec = typeof SubmitFlightBookingFlowSpec;

/**
 * The contract for the "ClearFlightBooking" flow.
 *
 * Deletes the user's FlightBooking for the given flightOptionId and clears
 * the itinerary item's `bookingId` link. Used when the user changes their
 * mind ("I didn't book after all").
 */
export const ClearFlightBookingFlowSpec = defineFlowSpec({
  name: 'ClearFlightBooking',
  input: z.object({ tripId: z.string(), flightOptionId: z.string() }),
  output: z.object({ success: z.boolean() }),
});
export type ClearFlightBookingFlowSpec = typeof ClearFlightBookingFlowSpec;

// =============================================================================
// Generic booking flows (places / activities / restaurants / custom)
// =============================================================================

const GenericBookingChannelSchema = z.enum(['manual', 'direct']);

/**
 * Records a booking for a non-hotel, non-flight itinerary item. Upserts a
 * `Booking` doc and back-links the item via `bookingId`. Used by the
 * "Mark as booked" affordance in the bookings tab.
 */
export const RecordBookingFlowSpec = defineFlowSpec({
  name: 'RecordBooking',
  // Sensitive action — gated behind legal-policy re-acceptance (defense-in-depth).
  requiresPolicyAcceptance: true,
  input: z.object({
    tripId: z.string(),
    itineraryItemId: z.string(),
    confirmationNumber: z.string().optional(),
    notes: z.string().optional(),
    channel: GenericBookingChannelSchema.optional(),
  }),
  output: z.object({ itineraryItemId: z.string() }),
});
export type RecordBookingFlowSpec = typeof RecordBookingFlowSpec;

/**
 * Updates fields on an existing generic Booking. Used by the inline edit
 * affordance on a booked item card.
 */
export const UpdateBookingFlowSpec = defineFlowSpec({
  name: 'UpdateBooking',
  // Sensitive action — gated behind legal-policy re-acceptance (defense-in-depth).
  requiresPolicyAcceptance: true,
  input: z.object({
    tripId: z.string(),
    itineraryItemId: z.string(),
    status: z.enum(['planned', 'booked', 'cancelled']).optional(),
    confirmationNumber: z.string().optional(),
    notes: z.string().optional(),
    channel: GenericBookingChannelSchema.optional(),
  }),
  output: z.object({ success: z.boolean() }),
});
export type UpdateBookingFlowSpec = typeof UpdateBookingFlowSpec;

/**
 * Clears a generic Booking — deletes the doc and unsets the linked item's
 * `bookingId`. Used when the user removes a booking record entirely.
 */
export const ClearBookingFlowSpec = defineFlowSpec({
  name: 'ClearBooking',
  input: z.object({ tripId: z.string(), itineraryItemId: z.string() }),
  output: z.object({ success: z.boolean() }),
});
export type ClearBookingFlowSpec = typeof ClearBookingFlowSpec;
